import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Check, FileCode2, ShieldCheck, Shuffle } from "lucide-react";
import { hasIr, hasTranslation, onWorkflowChange } from "@/lib/workflow";

/**
 * Visual progress meter for the Parse → Translate → Validate pipeline.
 * A step is:
 *  - <b>completed</b> when the workflow store carries the corresponding
 *    output (an IR for Parse, a translation for Translate);
 *  - <b>active</b> when the user is on its route;
 *  - <b>locked</b> otherwise — clicking it just no-ops, since the
 *    downstream pages have their own empty-state guards.
 *
 * <p>Rendered between the header and the page body; collapses on /login
 * so the auth screen stays uncluttered.
 */
type StepKey = "parse" | "translate" | "validate";

type StepDef = {
  key: StepKey;
  label: string;
  to: string;
  icon: React.ReactNode;
};

const STEPS: ReadonlyArray<StepDef> = [
  { key: "parse",     label: "Parse",     to: "/parse",     icon: <FileCode2 size={14} /> },
  { key: "translate", label: "Translate", to: "/translate", icon: <Shuffle size={14} /> },
  { key: "validate",  label: "Validate",  to: "/validate",  icon: <ShieldCheck size={14} /> },
];

export function Stepper() {
  const { pathname } = useLocation();

  // Re-render whenever the workflow store changes so completion ticks
  // light up immediately on Save, not after the next route transition.
  const [, force] = useState(0);
  useEffect(() => onWorkflowChange(() => force((n) => n + 1)), []);

  // Do not render on routes that aren't part of the wizard. /history and
  // /settings are post-wizard utilities; /login and / are pre-wizard.
  const onWizardRoute = ["/parse", "/translate", "/validate"].some((p) => pathname.startsWith(p));
  if (!onWizardRoute) return null;

  const irReady = hasIr();
  const translationReady = hasTranslation();

  function statusFor(key: StepKey): "done" | "active" | "locked" {
    const isActive = pathname.startsWith("/" + key);
    const isDone =
      (key === "parse" && irReady) ||
      (key === "translate" && translationReady) ||
      (key === "validate" && false); // validate doesn't yet self-mark
    if (isActive) return "active";
    return isDone ? "done" : "locked";
  }

  function unlocked(key: StepKey): boolean {
    if (key === "parse") return true;
    if (key === "translate") return irReady;
    return irReady; // validate is reachable as soon as we have an IR
  }

  return (
    <nav
      aria-label="Bridge workflow"
      className="border-b border-bridge-slate/15 bg-white/70"
    >
      <ol className="mx-auto flex max-w-5xl items-center gap-2 px-6 py-3">
        {STEPS.map((step, i) => {
          const status = statusFor(step.key);
          const enabled = unlocked(step.key);
          return (
            <li key={step.key} className="flex flex-1 items-center gap-2">
              <StepPill step={step} status={status} enabled={enabled} index={i + 1} />
              {i < STEPS.length - 1 && (
                <span
                  className={`h-px flex-1 ${
                    statusFor(STEPS[i + 1].key) === "locked" && status !== "done"
                      ? "bg-bridge-slate/20"
                      : "bg-bridge-primary/40"
                  }`}
                  aria-hidden
                />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function StepPill({
  step,
  status,
  enabled,
  index,
}: {
  step: StepDef;
  status: "done" | "active" | "locked";
  enabled: boolean;
  index: number;
}) {
  const tone =
    status === "active"
      ? "border-bridge-primary bg-bridge-primary text-white"
      : status === "done"
      ? "border-bridge-primary/30 bg-bridge-primary/10 text-bridge-primary"
      : "border-bridge-slate/30 bg-white text-bridge-slate";

  const inner = (
    <span
      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${tone} ${
        !enabled && status !== "active" ? "cursor-not-allowed opacity-70" : ""
      }`}
      aria-current={status === "active" ? "step" : undefined}
      title={
        status === "locked" && !enabled
          ? `Complete the previous step before continuing to ${step.label}.`
          : undefined
      }
    >
      <span
        className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] ${
          status === "active"
            ? "bg-white/20"
            : status === "done"
            ? "bg-bridge-primary text-white"
            : "bg-bridge-slate/15 text-bridge-slate"
        }`}
        aria-hidden
      >
        {status === "done" ? <Check size={11} /> : index}
      </span>
      <span className="inline-flex items-center gap-1.5">
        {step.icon}
        <span>{step.label}</span>
      </span>
    </span>
  );

  if (!enabled) return inner;
  return (
    <Link to={step.to} className="focus:outline-none focus-visible:ring-2 focus-visible:ring-bridge-primary/30 rounded-full">
      {inner}
    </Link>
  );
}
